import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, type DrawerProps, Space, Spin, Form, Result } from 'antd';
import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DealerListDataFragment } from '../../../../api/fragments/DealerListData';
import { useListDealersQuery } from '../../../../api/queries/listDealers';
import DrawerWithAutoWidth from '../../../DrawerWithAutoWidth';
import InputNumberField from '../../InputNumberField';
import { type SelectFieldProps } from '../../SelectField';
import { useAddDealershipModal } from '../AddDealershipModal';
import { type DealershipItem } from '../DealershipSelectField/DealershipDrawer';

export type DealershipDepositAmountDrawerProps = Pick<DrawerProps, 'title' | 'visible' | 'onClose'> &
    Pick<SelectFieldProps, 'options'> & {
        name: string;
        dealers?: DealerListDataFragment[];
        renderField?: (item: DealershipItem, onDelete: () => void, options: SelectFieldProps['options']) => JSX.Element;
    };

const DealershipDepositDrawer = ({
    onClose,
    title,
    visible,
    name,
    dealers: dealersFromProps,
    renderField = renderInputField,
    options,
}: DealershipDepositAmountDrawerProps) => {
    const { t } = useTranslation(['translationField']);

    const [{ value: dealerships }, , { setValue }] = useField<{ dealerId: string; value: string }[]>({
        name,
    });

    // to do - get dealers data
    const { data, loading } = useListDealersQuery({
        fetchPolicy: 'cache-and-network',
        skip: !!dealersFromProps,
    });

    const dealersFromQuery = data?.page?.items; // to do - change with dealers

    const allDealers = useMemo(() => dealersFromProps || dealersFromQuery || [], [dealersFromProps, dealersFromQuery]);

    const remainingDealers = useMemo(() => {
        if (!dealerships) {
            return allDealers;
        }

        return allDealers.filter(dealer => !dealerships.some(dealership => dealership.dealerId === dealer.id));
    }, [allDealers, dealerships]);

    const actions = useMemo(() => {
        const currentDealers = dealerships || [];

        return {
            add: (dealerId: string) => setValue([...currentDealers, { dealerId, value: null }]),
            delete: (index: number) => {
                const newValue = [...currentDealers];
                newValue.splice(index, 1);
                setValue(newValue);
            },
        };
    }, [setValue, dealerships]);

    const addModal = useAddDealershipModal(remainingDealers, actions.add);

    const cleanedDealerships = useMemo((): DealershipItem[] => {
        if (!dealerships) {
            return [];
        }

        return dealerships
            .map((dealership, index) => {
                const dealer = allDealers.find(dealer => dealer.id === dealership.dealerId);

                return { ...dealership, dealer, name: `${name}[${index}].value`, index, options };
            })
            .filter(dealership => !!dealership.dealer)
            .toSorted((a, b) => a.dealer.displayName.localeCompare(b.dealer.displayName));
    }, [dealerships, allDealers, name, options]);

    const content = (
        <Space direction="vertical" style={{ width: '100%' }}>
            {cleanedDealerships.length > 0 && (
                <Form layout="vertical">
                    {cleanedDealerships.map(dealership =>
                        renderField(dealership, () => actions.delete(dealership.index), options)
                    )}
                </Form>
            )}
            {remainingDealers.length > 0 && (
                <Button icon={<PlusOutlined />} onClick={addModal.open} type="dashed" block>
                    {t('dealershipField:default.addDealer')}
                </Button>
            )}
            {addModal.render()}
            {!cleanedDealerships.length && !remainingDealers.length && (
                <Result status="info" subTitle="No dealer registered in the system yet" />
            )}
        </Space>
    );

    return (
        <DrawerWithAutoWidth onClose={onClose} open={visible} placement="right" title={title} destroyOnClose>
            <Spin spinning={loading}>{content}</Spin>
        </DrawerWithAutoWidth>
    );
};

export const renderInputField: DealershipDepositAmountDrawerProps['renderField'] = (market, onDelete) => (
    <InputNumberField
        key={market.dealerId}
        addonAfter={<Button icon={<DeleteOutlined />} onClick={onDelete} size="small" type="text" />}
        label={market.dealer.displayName}
        name={market.name}
    />
);

export default DealershipDepositDrawer;

export const useDealershipDepositAmountDrawer = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (
            props: Pick<DealershipDepositAmountDrawerProps, 'name' | 'title' | 'renderField' | 'dealers' | 'options'>
        ) => (
            <DealershipDepositDrawer
                {...props}
                name={`${props.name}.overrides`}
                onClose={actions.close}
                visible={visible}
            />
        ),
    };
};
